import { useState, useCallback } from "react";

/**
 * Hook for managing selected folder ID state
 *
 * Simple state management for the currently selected folder in a folder tree.
 * Handles selection, clearing, and provides getter for current selection.
 *
 * @returns Object with selectedFolderId, selectFolder, and clearSelection
 *
 * @example
 * ```tsx
 * const { selectedFolderId, selectFolder, clearSelection } = useFolderSelection();
 *
 * return (
 *   <div>
 *     <button onClick={() => selectFolder("folder-123")}>Select Folder</button>
 *     <button onClick={clearSelection}>Clear</button>
 *     <p>Selected: {selectedFolderId || "None"}</p>
 *   </div>
 * );
 * ```
 */
export function useFolderSelection(initialFolderId: string | null = null) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(initialFolderId);

  const selectFolder = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedFolderId(null);
  }, []);

  const isSelected = useCallback(
    (folderId: string) => selectedFolderId === folderId,
    [selectedFolderId]
  );

  return {
    selectedFolderId,
    selectFolder,
    clearSelection,
    isSelected,
  };
}
